import React, { FC, useCallback, useContext, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Descendant, Element } from 'slate';
import { v4 as uuid } from 'uuid';

import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';

import { FormulaType } from '../../../../../constants/formulaType';
//constants
import routes from '../../../../../constants/routes';

//context
import { EditorContext } from '../../../../../context/EditorContext';

import alternateQueryKeys from '../../../../../hooks/alternates/_queryKeys';
import useFormulaCreateMutation from '../../../../../hooks/formulas/useFormulaCreateMutation';
import useQueryParams from '../../../../../hooks/helper/useQueryParams';
import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';
import useBulkDeleteVariantByComponentId from 'hooks/variants/useBulkDeleteVariantByComponentId';
import useVariantBulkCreateMutation from 'hooks/variants/useVariantCreateBulkMutation';
import useVariantUpdateBulkMutation from 'hooks/variants/useVariantUpdateBulkMutation';

import { VariantUpdateModel } from '../../../../../models/alternate.model';
import {
    ElementKind,
    ImageVariantElement,
    SectionElement,
} from '../../../../../models/editor.model';
//models
import {
    ContentCardType,
    FormulaTypesData,
} from '../../../../../models/formula.model';
import { VariantType } from '../../../../../models/variant.model';

//query
import { queryClient } from '../../../../../AppComposer';
//components
import ImageAlternateModal from './ImageAlternateModal';

interface ImageAlternateContainerProps {
    handleUpdateStructure: (newValue: Descendant[]) => void;
    showImageAlternateIconModal: boolean;
    setShowImageAlternateIconModal: (value: boolean) => void;
    elementData?: ImageVariantElement;
    isEditable: boolean;
}

const ImageAlternateContainer: FC<ImageAlternateContainerProps> = ({
    handleUpdateStructure,
    showImageAlternateIconModal,
    setShowImageAlternateIconModal,
    elementData,
    isEditable,
}) => {
    const history = useHistory();
    const { message_type = FormulaType.mobilePush } = useQueryParams();
    const {
        editorDetails: { formula, formulaType, isApproved },
    } = useContext(EditorContext);
    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );

    const element = useMemo<ImageVariantElement>(() => {
        if (elementData) {
            return elementData;
        }
        return formula?.structure?.find(
            (i: any) => i?.kind === ElementKind.image,
        ) as ImageVariantElement;
    }, [elementData, formula?.structure]);

    const inputType: any = ElementKind.image;

    const formulaIdRef = useRef<number>(formula?.id!);
    const isNewFormulaRef = useRef<boolean>(false);

    const { data = [], isLoading } = useVariantsByComponentIdQuery(element.id);

    const messageContentUpdate = useMessageContentGetAndUpdate();

    const createFormula = useFormulaCreateMutation(message_type);

    const variantBulkCreateMutation = useVariantBulkCreateMutation(
        formulaIdRef.current,
        element.id,
    );

    const bulkVariantDeleteMutation = useBulkDeleteVariantByComponentId(
        element.id,
        formulaIdRef.current,
    );

    const bulkVariantUpdateMutation = useVariantUpdateBulkMutation(
        element.id,
        formulaIdRef.current,
    );

    const contentCardType = useMemo<ContentCardType | undefined>(() => {
        if (message_type === FormulaType.contentCards) {
            return formula?.content_card_type || ContentCardType.TextOnly;
        }
    }, [formula, message_type]);

    const originalStructure = useMemo<Descendant[]>(
        () =>
            formulaIdRef.current && formula?.structure
                ? formula.structure
                : FormulaTypesData[message_type as FormulaType].getTemplate({
                      content_card_type: contentCardType,
                  }),
        [formula, message_type, contentCardType],
    );

    const getCurrentElementIndex = useCallback(() => {
        const structure = [...(originalStructure ?? [])];
        if (
            [FormulaType.mobilePush, FormulaType.contentCards].includes(
                formulaType as FormulaType,
            )
        ) {
            return structure.findIndex(
                (item) => (item as Element)?.kind === ElementKind.image,
            );
        } else if (formulaType === FormulaType.email) {
            return structure.findIndex(
                (item) =>
                    'kind' in item &&
                    item.kind === ElementKind.section &&
                    'variantType' in item &&
                    item.variantType === VariantType.Image,
            );
        }
        return structure.findIndex((item) => {
            const ele = item as SectionElement;
            const isSection = ele?.kind === ElementKind.section;
            const isImage = ele?.sectionType === inputType;
            return isSection && isImage;
        });
    }, [formulaType, inputType, originalStructure]);

    const getUpdateStructure = useCallback(
        (id: string, text: string, labelId?: number) => {
            const structure = [...(originalStructure ?? [])];
            const elementIndex = getCurrentElementIndex();
            if (elementIndex !== -1) {
                const newElement: ImageVariantElement = {
                    ...element,
                    variantType: VariantType.Image,
                    children: [{ text }],
                    id,
                    labelId,
                };
                const elementText =
                    (structure?.[elementIndex] as Element)?.children?.[0]
                        ?.text ?? '';
                if (elementText !== text) {
                    let updatedStructure = [...structure];
                    updatedStructure[elementIndex] = newElement;
                    return updatedStructure;
                }
                return;
            }
            return structure;
        },
        [element, getCurrentElementIndex, originalStructure],
    );

    const handleCreateUpdateAlternates = useCallback(
        async (
            data: VariantUpdateModel[],
            componentId: string,
            callback?: () => Promise<void>,
        ) => {
            const createAlternates = data.filter((item) =>
                item.id.includes('ALTERNATE_'),
            );
            const updateAlternates = data.filter(
                (item) => !item.id.includes('ALTERNATE_'),
            ) as VariantUpdateModel[];
            if (updateAlternates.length) {
                const payload = updateAlternates.map((variant) => ({
                    id: variant.id,
                    content: variant.text,
                    label_id: variant.label_id || null,
                }));
                await bulkVariantUpdateMutation.mutateAsync(payload);
            }
            if (createAlternates.length) {
                const payload = createAlternates.map((variant) => ({
                    component_id: componentId,
                    content: variant.text,
                    label_id: variant.label_id,
                }));
                await variantBulkCreateMutation.mutateAsync(payload);
            }

            await callback?.();
        },
        [bulkVariantUpdateMutation, variantBulkCreateMutation],
    );

    const handleCreateMasterAndAddAlternates = useCallback(
        async (data: VariantUpdateModel[]) => {
            const createData = data;
            const master = createData.find((item) => item.is_master);
            const id = uuid();
            if (master) {
                const updatedStructure = getUpdateStructure(
                    id,
                    master.text,
                    master.label_id,
                );

                if (updatedStructure) {
                    await messageContentUpdate.mutateAsync({
                        id: currentMessageContentId!,
                        structure: updatedStructure,
                    });
                }
                handleCreateUpdateAlternates(
                    createData.filter((item) => !item.is_master),
                    id,
                    async () => {
                        if (isNewFormulaRef.current) {
                            history.replace(
                                routes.formulaDraft(formulaIdRef.current),
                            );
                        } else {
                            setShowImageAlternateIconModal(false);
                            await queryClient.invalidateQueries({
                                queryKey:
                                    alternateQueryKeys.getVariantsByComponentId(
                                        id,
                                    ),
                            });
                        }
                    },
                );
            }
        },
        [
            getUpdateStructure,
            handleCreateUpdateAlternates,
            history,
            messageContentUpdate,
            setShowImageAlternateIconModal,
            currentMessageContentId,
        ],
    );

    const handleAddAlternates = useCallback(
        async (data: VariantUpdateModel[]) => {
            if (!formulaIdRef.current) {
                const { data } = await createFormula.mutateAsync({
                    structure: FormulaTypesData[
                        message_type as FormulaType
                    ].getTemplate({ content_card_type: contentCardType }),
                });
                if (data) {
                    formulaIdRef.current = data.id;
                    isNewFormulaRef.current = true;
                }
            }
            const alternates = data.filter((item) => !item.deleted);
            if (element?.id) {
                const deletedVariants = (data as VariantUpdateModel[]).filter(
                    (item) => item.deleted && item.id,
                );
                handleCreateUpdateAlternates(
                    alternates,
                    element?.id,
                    async () => {
                        if (deletedVariants.length) {
                            await bulkVariantDeleteMutation.mutateAsync({
                                variant_ids: deletedVariants.map(
                                    (item) => item.id,
                                ),
                            });
                        }
                        if (isNewFormulaRef.current) {
                            history.replace(
                                routes.formulaDraft(formulaIdRef.current),
                            );
                        } else {
                            setShowImageAlternateIconModal(false);
                        }
                    },
                );
                return;
            }
            handleCreateMasterAndAddAlternates(alternates);
        },
        [
            element?.id,
            handleCreateMasterAndAddAlternates,
            createFormula,
            message_type,
            contentCardType,
            handleCreateUpdateAlternates,
            bulkVariantDeleteMutation,
            history,
            setShowImageAlternateIconModal,
        ],
    );

    const handleDeleteMaster = useCallback(async () => {
        const structure = [...(originalStructure ?? [])];
        const elementIndex = getCurrentElementIndex();
        if (elementIndex !== -1) {
            const newElement = {
                ...element,
                children: [{ text: '' }],
            } as Partial<ImageVariantElement>;
            delete newElement['id'];
            delete newElement['componentId'];
            let updatedStructure = [...structure];
            updatedStructure[elementIndex] = newElement as Element;
            messageContentUpdate.mutate(
                {
                    id: currentMessageContentId!,
                    structure: updatedStructure,
                },
                {
                    onSuccess: () => {
                        handleUpdateStructure(updatedStructure);
                        setShowImageAlternateIconModal(false);
                    },
                },
            );
        }
    }, [
        originalStructure,
        getCurrentElementIndex,
        element,
        messageContentUpdate,
        handleUpdateStructure,
        setShowImageAlternateIconModal,
        currentMessageContentId,
    ]);

    return (
        <>
            {showImageAlternateIconModal && !isLoading && (
                <ImageAlternateModal
                    data={data}
                    formulaId={formulaIdRef.current}
                    handleAddAlternates={handleAddAlternates}
                    handleClose={() => setShowImageAlternateIconModal(false)}
                    isLoading={isApproved}
                    handleDeleteMaster={handleDeleteMaster}
                    isExisting={!!element?.id}
                    isEditable={isEditable}
                />
            )}
        </>
    );
};

export default ImageAlternateContainer;
